import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthProvider";
import { ThemeProvider } from "@/contexts/ThemeProvider";
import { OrgProvider } from "@/contexts/OrgProvider";
import { useOrg } from "@/contexts/OrgContext";
import { CUSTOM_DOMAINS_ENABLED } from "@/config/featureFlags";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import Index from "./pages/Index";
import MarketingHome from "./pages/MarketingHome";
import MarketingFeatures from "./pages/MarketingFeatures";
import MarketingPricing from "./pages/MarketingPricing";
import MarketingFAQ from "./pages/MarketingFAQ";
import MarketingAbout from "./pages/MarketingAbout";
import SalesContact from "./pages/SalesContact";
import SubdomainRedirect from "./pages/SubdomainRedirect";
import ServicesPage from "./pages/Services";
import AboutPage from "./pages/About";
import BookingPage from "./pages/Booking";
import ContactPage from "./pages/Contact";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Signup from "./pages/Signup";
import TeamSetup from "./pages/TeamSetup";
import CustomerDashboard from "./pages/CustomerDashboard";
import CustomPage from "./pages/CustomPage";
import OrgPage from "./pages/OrgPage";
import { PrivacyPolicy, TermsAndConditions, CookiePolicy } from "./pages/Legal";
import AdminLayout from "./components/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import HomepageAdmin from "./pages/admin/HomepageAdmin";
import AboutAdmin from "./pages/admin/AboutAdmin";
import ServicesAdmin from "./pages/admin/ServicesAdmin";
import JobsPricingAdmin from "./pages/admin/JobsPricingAdmin";
import ReviewsAdmin from "./pages/admin/ReviewsAdmin";
import SeoAdmin from "./pages/admin/SeoAdmin";
import PaymentsAdmin from "./pages/admin/PaymentsAdmin";
import BillingAdmin from "./pages/admin/BillingAdmin";
import DataAdmin from "./pages/admin/DataAdmin";
import OperationalHoursAdmin from "./pages/admin/OperationalHoursAdmin";
import TeamAdmin from "./pages/admin/TeamAdmin";
import ScheduleAdmin from "./pages/admin/ScheduleAdmin";
import RepeatCleansAdmin from "./pages/admin/RepeatCleansAdmin";
import ChecklistsAdmin from "./pages/admin/ChecklistsAdmin";
import MessagesAdmin from "./pages/admin/MessagesAdmin";
import CompanyInfoAdmin from "./pages/admin/CompanyInfoAdmin";
import EnquiriesAdmin from "./pages/admin/EnquiriesAdmin";
import CustomersAdmin from "./pages/admin/CustomersAdmin";
import InvoicesAdmin from "./pages/admin/InvoicesAdmin";
import QuotesAdmin from "./pages/admin/QuotesAdmin";
import QuoteEditorAdmin from "./pages/admin/QuoteEditorAdmin";
import QuotePage from "./pages/QuotePage";
import ThemeAdmin from "./pages/admin/ThemeAdmin";
import PageBuilderAdmin from "./pages/admin/PageBuilderAdmin";
import DomainAdmin from "./pages/admin/DomainAdmin";
import SmsAdmin from "./pages/admin/SmsAdmin";
import EditorFrame from "./pages/admin/EditorFrame";
import ThemePreviewFrame from "./pages/admin/ThemePreviewFrame";
import RequirePlatformAdmin from "./components/platform/RequirePlatformAdmin";
import PlatformLayout from "./pages/platform/PlatformLayout";
import PlatformOverview from "./pages/platform/PlatformOverview";
import TenantList from "./pages/platform/TenantList";
import TenantDetail from "./pages/platform/TenantDetail";
import AuditLog from "./pages/platform/AuditLog";
import PlatformSettings from "./pages/platform/PlatformSettings";
import PlatformSupport from "./pages/platform/PlatformSupport";
import PlatformSection from "./pages/platform/PlatformSection";
import PlatformFinancials from "./pages/platform/PlatformFinancials";
import UATChecklist from "./pages/platform/UATChecklist";
import SmokeChecklist from "./pages/platform/SmokeChecklist";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

/**
 * Decides what renders at "/":
 *  - Subdomain that doesn't resolve → redirect to apex
 *  - Apex with no tenant context → SaaS marketing site
 *  - Otherwise (tenant subdomain or admin context) → tenant homepage
 */
const RootRoute = () => {
  const { isMarketingSite, isUnknownSubdomain, isSuspended } = useOrg();
  if (isUnknownSubdomain) return <SubdomainRedirect />;
  if (isMarketingSite) return <MarketingHome />;
  if (isSuspended) return <SuspendedTenantNotice />;
  return <Index />;
};

const DemoRoute = ({ element }: { element: React.ReactElement }) => {
  const { isUnknownSubdomain } = useOrg();
  if (isUnknownSubdomain) return <SubdomainRedirect />;
  return element;
};

const SuspendedTenantNotice = () => (
  <main className="min-h-screen bg-background px-6 py-20 text-foreground">
    <div className="mx-auto max-w-xl rounded-lg border border-border bg-card p-8 text-center shadow-sm">
      <h1 className="font-display text-3xl text-foreground">This site is temporarily unavailable</h1>
      <p className="mt-3 text-muted-foreground">Please contact the business owner for more information.</p>
    </div>
  </main>
);

const TenantRoute = ({ element }: { element: React.ReactElement }) => {
  const { isSuspended, isImpersonating } = useOrg();
  if (isSuspended && !isImpersonating) return <SuspendedTenantNotice />;
  return element;
};

/** Marketing-only routes redirect to /demo (or apex) when on a tenant subdomain. */
const MarketingOnlyRoute = ({ element }: { element: React.ReactElement }) => {
  const { isMarketingSite, isUnknownSubdomain } = useOrg();
  if (isUnknownSubdomain) return <SubdomainRedirect />;
  if (!isMarketingSite) return <Index />; // tenant context → show their homepage
  return element;
};

/** /about: marketing site → sales About; tenant → tenant About page (same style as /demo/about) */
const AboutRoute = () => {
  const { isMarketingSite, isUnknownSubdomain, isSuspended, isImpersonating } = useOrg();
  if (isUnknownSubdomain) return <SubdomainRedirect />;
  if (isMarketingSite) return <MarketingAbout />;
  if (isSuspended && !isImpersonating) return <SuspendedTenantNotice />;
  return <AboutPage />;
};

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <OrgProvider>
          <ThemeProvider>
            <TooltipProvider>
              <ScrollToTop />
              <Toaster />
              <Sonner />
              <Routes>
                <Route path="/" element={<RootRoute />} />
                {/* Demo: always renders the Polished Version tenant homepage */}
                <Route path="/demo" element={<DemoRoute element={<Index />} />} />
                <Route path="/demo/services" element={<DemoRoute element={<ServicesPage />} />} />
                <Route path="/demo/about" element={<DemoRoute element={<AboutPage />} />} />
                <Route path="/demo/booking" element={<DemoRoute element={<BookingPage />} />} />
                <Route path="/demo/contact" element={<DemoRoute element={<ContactPage />} />} />
                {/* Marketing-only pages (apex domain) */}
                <Route path="/features" element={<MarketingOnlyRoute element={<MarketingFeatures />} />} />
                <Route path="/pricing" element={<MarketingOnlyRoute element={<MarketingPricing />} />} />
                <Route path="/faq" element={<MarketingOnlyRoute element={<MarketingFAQ />} />} />
                <Route path="/about" element={<AboutRoute />} />
                <Route path="/sales-contact" element={<MarketingOnlyRoute element={<SalesContact />} />} />
                <Route path="/services" element={<TenantRoute element={<ServicesPage />} />} />
                <Route path="/booking" element={<TenantRoute element={<BookingPage />} />} />
                <Route path="/contact" element={<TenantRoute element={<ContactPage />} />} />
                <Route path="/customer" element={<TenantRoute element={<CustomerDashboard />} />} />
                {/* Legal pages — render the correct chrome (marketing vs tenant) internally. */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/signup" element={<Signup />} />
                {/* Legacy: /register-business now points to the polished /signup wizard */}
                <Route path="/register-business" element={<Signup />} />
                <Route path="/team-setup" element={<TeamSetup />} />

                <Route path="/admin/editor-frame" element={<EditorFrame />} />
                <Route path="/admin/theme-preview-frame" element={<ThemePreviewFrame />} />

                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="company-info" element={<CompanyInfoAdmin />} />
                  <Route path="theme" element={<ThemeAdmin />} />
                  <Route path="page-builder" element={<PageBuilderAdmin />} />
                  <Route path="pages" element={<PageBuilderAdmin />} />
                  <Route path="homepage" element={<HomepageAdmin />} />
                  <Route path="about" element={<AboutAdmin />} />
                  <Route path="services" element={<ServicesAdmin />} />
                  <Route path="jobs-pricing" element={<JobsPricingAdmin />} />
                  <Route path="checklists" element={<ChecklistsAdmin />} />
                  <Route path="reviews" element={<ReviewsAdmin />} />
                  <Route path="seo" element={<SeoAdmin />} />
                  <Route path="payments" element={<PaymentsAdmin />} />
                  <Route path="billing" element={<BillingAdmin />} />
                  <Route path="data" element={<DataAdmin />} />
                  <Route path="enquiries" element={<EnquiriesAdmin />} />
                  <Route path="customers" element={<CustomersAdmin />} />
                  <Route path="invoices" element={<InvoicesAdmin />} />
                  <Route path="quotes" element={<QuotesAdmin />} />
                  <Route path="quotes/:quoteId" element={<QuoteEditorAdmin />} />
                  <Route path="operational-hours" element={<OperationalHoursAdmin />} />
                  <Route path="team" element={<TeamAdmin />} />
                  <Route path="schedule" element={<ScheduleAdmin />} />
                  <Route path="repeat-cleans" element={<RepeatCleansAdmin />} />
                  <Route path="messages" element={<MessagesAdmin />} />
                  <Route path="text-messages" element={<SmsAdmin />} />
                  {/* Custom domains (owners; Solo sees an upgrade prompt). The flag
                      can hide it again by steering owners back to the admin shell. */}
                  <Route
                    path="domain"
                    element={CUSTOM_DOMAINS_ENABLED ? <DomainAdmin /> : <Navigate to="/admin" replace />}
                  />
                </Route>

                <Route path="/platform" element={<RequirePlatformAdmin />}>
                  <Route element={<PlatformLayout />}>
                    <Route index element={<PlatformOverview />} />
                    <Route path="tenants" element={<TenantList />} />
                    <Route path="tenants/:orgId" element={<TenantDetail />} />
                    <Route path="customers" element={<PlatformSection kind="customers" title="Customers" description="Platform-wide customer account view across organisations." />} />
                    <Route path="bookings" element={<PlatformSection kind="bookings" title="Bookings" description="Platform-wide booking and request view across organisations." />} />
                    <Route path="messages" element={<PlatformSection kind="messages" title="Messages" description="Platform-wide message thread view across enquiries and bookings." />} />
                    {/* PA-0.3: no partial totals — see PlatformFinancials. /platform/billing reserved for PA-5.1. */}
                    <Route path="financials" element={<PlatformFinancials />} />
                    <Route path="audit" element={<AuditLog />} />
                    <Route path="uat-checklist" element={<UATChecklist />} />
                    <Route path="smoke-checklist" element={<SmokeChecklist />} />
                    <Route path="support" element={<PlatformSupport />} />
                    <Route path="settings" element={<PlatformSettings />} />
                  </Route>
                </Route>

                {/* Custom Pages - must be before catch-all */}
                <Route path="/p/:slug" element={<TenantRoute element={<CustomPage />} />} />

                {/* Public priced quote (token link emailed to the customer) */}
                <Route path="/quote/:token" element={<TenantRoute element={<QuotePage />} />} />

                {/* Organization public booking pages */}
                <Route path="/org/:slug" element={<TenantRoute element={<OrgPage />} />} />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </TooltipProvider>
          </ThemeProvider>
        </OrgProvider>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
